import { Check, Store as StoreIcon } from "lucide-react";
import { de, ketten, type ChainKey, type Store } from "@ainkauf/core";

/**
 * "Wo kaufst du gerade ein?"
 *
 * Ein Blatt statt eines Auswahlfelds: Im Laden wird mit dem Daumen getippt,
 * und jede Zeile ist so hoch, dass man sie im Gehen trifft. Auswählen ist
 * freiwillig; ohne Laden sortiert die Liste wie bisher.
 *
 * Oben die Läden, die die Familie schon hat, darunter die Ketten mit den
 * dreien zuerst, in denen sie am meisten einkauft.
 */
export function StoreSheet({
  stores,
  activeStoreId,
  onChoose,
  onCancel,
}: {
  stores: readonly Store[];
  activeStoreId: string | null;
  onChoose: (wahl: { storeId: string } | { chain: ChainKey } | { keiner: true }) => void;
  onCancel: () => void;
}) {
  const eigeneKetten = new Set(stores.map((laden) => laden.chain).filter((kette): kette is ChainKey => kette !== null));
  const offeneKetten = ketten().filter((profil) => !eigeneKetten.has(profil.key));

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={onCancel} role="presentation">
      <div
        onClick={(event) => {
          event.stopPropagation();
        }}
        role="dialog"
        aria-label={de.trip.storeQuestion}
        data-testid="store-sheet"
        className="flex max-h-[85dvh] flex-col gap-4 overflow-y-auto rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <div>
          <h2 className="text-xl">{de.trip.storeQuestion}</h2>
          <p className="mt-1 text-sm text-ink-2">{de.trip.storeHint}</p>
        </div>

        {stores.length > 0 && (
          <section className="flex flex-col gap-1.5">
            <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-ink-2">{de.trip.storeOwn}</h3>
            {stores.map((laden) => (
              <Zeile
                key={laden.id}
                name={laden.name}
                aktiv={laden.id === activeStoreId}
                testId="store-option"
                onClick={() => {
                  onChoose({ storeId: laden.id });
                }}
              />
            ))}
          </section>
        )}

        <section className="flex flex-col gap-1.5">
          <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-ink-2">{de.trip.storeChains}</h3>
          {offeneKetten.map((profil) => (
            <Zeile
              key={profil.key}
              name={profil.name}
              aktiv={false}
              testId="store-chain"
              onClick={() => {
                onChoose({ chain: profil.key });
              }}
            />
          ))}
        </section>

        <Zeile
          name={de.trip.storeNone}
          aktiv={activeStoreId === null}
          testId="store-none"
          onClick={() => {
            onChoose({ keiner: true });
          }}
        />

        <button
          type="button"
          onClick={onCancel}
          className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 font-semibold text-ink-2"
        >
          {de.common.cancel}
        </button>
      </div>
    </div>
  );
}

function Zeile({ name, aktiv, testId, onClick }: { name: string; aktiv: boolean; testId: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktiv}
      data-testid={testId}
      data-name={name}
      className={`flex min-h-[52px] w-full items-center gap-3 rounded-[var(--radius-lg)] px-4 text-left transition-transform active:scale-[0.99] ${
        aktiv ? "bg-accent/12 text-ink" : "bg-card text-ink shadow-[var(--shadow-card)]"
      }`}
    >
      <StoreIcon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-ink-3" />
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
      {aktiv && <Check size={18} strokeWidth={2.25} aria-hidden className="shrink-0 text-accent" />}
    </button>
  );
}
